import { Address } from "@olga-food/schemas/lib/classes/schemas/address/Address";
import { Geolocation } from "@olga-food/schemas/lib/classes/schemas/address/Geolocation";
import {
  Delivery,
  DeliveryType,
} from "@olga-food/schemas/lib/classes/schemas/order/delivery/Delivery";
import { DeliveryStrip } from "@olga-food/schemas/lib/classes/schemas/order/delivery/DeliveryStrip";
import { DeliveryChecker } from "shared/tools/DeliveryChecker";
import { Time } from "shared/tools/Time";
import { EnvironmentsAPI } from "./EnvironmentsAPI";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";

export class BucketsAPI {
  static async getNearbyUnits(geolocation) {
    const customerLocation = Geolocation.fromObject(geolocation);
    const environment = await EnvironmentStorage.getEnvironment();

    const unit = await EnvironmentsAPI.getS3Configurations(environment.alias);

    const filteredUnits = [];
    [unit].forEach((unit) => {
      const {
        active,
        address,
        delivery_regions,
        delivery_polygons,
        working_time,
        delivery_time,
      } = unit;

      if (
        !active ||
        !address ||
        !delivery_regions ||
        !working_time ||
        delivery_time?.allowDelivery === false
      ) {
        return false;
      }

      const checker = new DeliveryChecker({
        address,
        deliveryRegions: delivery_regions || [],
        deliveryPolygons: delivery_polygons || [],
      });

      const isInsideLimits = checker.isInsideLimits(customerLocation);

      const currentDate = Time.getInstance();
      const days = working_time.days || [];
      const isOpen = BucketsAPI.isOpen(
        currentDate.day(),
        currentDate.format("HHmm"),
        days,
        unit,
      );

      const unitAddress = Address.fromObject(address);
      if (!unitAddress.geolocation) {
        return false;
      }

      const nearbyRegion = checker.isInsideDeliveryStrips(customerLocation);
      const deliveryStrip = DeliveryStrip.fromObject(nearbyRegion);
      deliveryStrip.min_order = nearbyRegion?.minOrder;

      if (delivery_polygons?.length > 0) {
        const poly = checker.getInsidePolygon(customerLocation);
        deliveryStrip.price = poly?.price || deliveryStrip?.price;
      }

      const delivery = Delivery.fromObject({
        address: checker.getAddress(),
        distance: checker.getDistance(customerLocation) / 1000.0,
        type: DeliveryType.DELIVER_ON_ADDRESS,
        total: deliveryStrip.price,
        deliveryStrip,
      });

      filteredUnits.push({
        unit: {
          id: unit.id,
          name: unit.name,
          alias: unit.alias,
          city: unitAddress.city,
          ...(unit?.deliveryTime?.hasOwnProperty("allowDelivery")
            ? { allowDelivery: unit.deliveryTime.allowDelivery }
            : { allowDelivery: true }),
          ...(unit?.deliveryTime?.hasOwnProperty("allowLocation")
            ? { allowLocation: unit.deliveryTime.allowLocation }
            : { allowLocation: false }),
          ...(unit?.deliveryTime?.hasOwnProperty("allowFastfila")
            ? { allowFastfila: unit.deliveryTime.allowFastfila }
            : { allowFastfila: false }),
          isOpen,
          isOpenForSchedule: !isOpen && unit?.schedule?.active,
          near: nearbyRegion && isInsideLimits ? true : false,
          shippinghub: unit.shippinghub,
        },
        delivery,
      });
    });

    return filteredUnits.sort((prev, next) => {
      if (prev.unit.isOpen === next.unit.isOpen) {
        return prev.delivery.distance - next.delivery.distance;
      }
      return prev.unit.isOpen ? -1 : 1;
    });
  }

  static isOpen(weekDay, hour, workingTime, unit) {
    let available = false;
    let currentTimeInt = parseInt(hour);

    const adjustedWeekDay = weekDay === 0 ? 7 : weekDay;

    if (
      !workingTime[adjustedWeekDay - 1]?.active ||
      adjustedWeekDay < 1 ||
      adjustedWeekDay > 7 ||
      currentTimeInt < 0 ||
      currentTimeInt >= 2400 ||
      !workingTime ||
      workingTime.length === 0
    ) {
      return available;
    }

    let yesterdayNum = adjustedWeekDay - 1 === 0 ? 7 : adjustedWeekDay - 1;

    const yesterday = workingTime.find(
      (day) => day.active && day.nextDay && +day.weeknum === yesterdayNum,
    );
    const today = workingTime.find(
      (day) => day.active && +day.weeknum === adjustedWeekDay,
    );

    if (!today) {
      return available;
    }

    if (yesterday) {
      const yesterdayClose = parseInt(
        yesterday.close[yesterday.close.length - 1],
      );
      if (currentTimeInt < yesterdayClose) {
        available = true;
        return available;
      }
    }

    for (
      let openTimeIndex = 0;
      openTimeIndex < today.open.length;
      openTimeIndex++
    ) {
      const open = parseInt(today.open[openTimeIndex]);
      let close = parseInt(today.close[openTimeIndex]);

      if (today.nextDay) {
        close = 2400;
      }

      // log(open, close, currentTimeInt, currentTimeInt >= open, currentTimeInt < close);

      if (currentTimeInt >= open && currentTimeInt < close) {
        available = true;
        return available;
      }
    }

    return available;
  }
}
